import React from 'react';
import { ChatEmpty24Regular, DismissCircle24Filled } from '@fluentui/react-icons';
import styles from './moduleEmptyState.module.css';

const MODULE_COPY = {
    highlights: {
        label: 'Highlights',
        notGeneratedMessage:
            'Generate an analysis version from Overview to populate Highlights with headline conclusions across Team, Technology and Cybersecurity.',
    },
    management_summary: {
        label: 'Management Summary',
        notGeneratedMessage:
            'Generate an analysis version from Overview to populate Management Summary with the overall assessment, takeaways and scope matrix.',
    },
    key_findings: {
        label: 'Key Findings',
        notGeneratedMessage:
            'Generate an analysis version from Overview to populate Key Findings with positives, concerns and unresolved matters by scope.',
    },
    swot: {
        label: 'SWOT Analysis',
        notGeneratedMessage:
            'Generate an analysis version from Overview to populate SWOT Analysis with evidence-backed strengths, weaknesses, opportunities and threats.',
    },
    investment_opinion: {
        label: 'Investment Opinion',
        notGeneratedMessage:
            'Generate an analysis version from Overview to populate Investment Opinion with deal posture and investment recommendation.',
    },
    remediation_investment: {
        label: 'Remediation & Investment',
        notGeneratedMessage:
            'Generate an analysis version from Overview to populate Remediation & Investment with recommended treatments, timing and indicative CAPEX/OPEX.',
    },
    risks_signals: {
        label: 'Risks & Signals',
        notGeneratedMessage:
            'Generate an analysis version from Overview to populate Risks & Signals with the material risk register and emerging signals.',
    },
    evidence_follow_ups: {
        label: 'Evidence & Follow-Ups',
        notGeneratedMessage:
            'Generate an analysis version from Overview to populate Evidence & Follow-Ups with evidence coverage, gaps and required follow-ups.',
    },
};

function resolveVariant({ variant, emptyState, error, status }) {
    if (variant) return variant;
    if (error) return 'failed';
    const type = String(emptyState?.type || status || '').toLowerCase();
    if (type === 'failed' || type.includes('fail')) return 'failed';
    return 'not_generated';
}

/**
 * Shared Insights empty / failed state (Figma Highlights_Not generated + Highlights_failed).
 * Generation always happens from Overview — modules link back there when not yet generated.
 */
export default function ModuleEmptyState({
    moduleKey = 'highlights',
    moduleLabel = null,
    variant = null,
    emptyState = null,
    error = null,
    status = null,
    loading = false,
    generating = false,
    meta = null,
    onGoToOverview = null,
    onGenerateAnalysis = null,
    onRetry = null,
}) {
    if (loading) {
        return (
            <div className={styles.emptyWrap}>
                <div className={styles.emptyInner}>
                    <p className={styles.loadingText}>Loading module…</p>
                </div>
            </div>
        );
    }

    const copy = MODULE_COPY[moduleKey] || {
        label: moduleLabel || 'Module',
        notGeneratedMessage:
            'Generate an analysis version from Overview to populate all Intelligence Insights modules together.',
    };
    const label = moduleLabel || emptyState?.moduleLabel || copy.label;
    const mode = resolveVariant({ variant, emptyState, error, status });
    const isFailed = mode === 'failed';

    const title =
        emptyState?.title ||
        (isFailed ? `${label} could not be generated` : `${label} not yet generated`);
    const message =
        (typeof error === 'string' && error) ||
        emptyState?.message ||
        (isFailed
            ? 'Generation failed. Retry from Overview to regenerate all modules using the same source snapshot.'
            : copy.notGeneratedMessage);
    const metaLine = meta || emptyState?.meta || null;

    const actionLabel = isFailed
        ? emptyState?.primaryAction?.label ||
          emptyState?.primary_action?.label ||
          'Retry analysis from Overview →'
        : 'Go to Overview →';

    const handlePrimary = () => {
        if (isFailed) {
            if (onRetry) onRetry();
            else onGenerateAnalysis?.();
            return;
        }
        onGoToOverview?.();
    };

    const canAct = Boolean(isFailed ? onGenerateAnalysis || onRetry : onGoToOverview);

    return (
        <div className={styles.emptyWrap}>
            <div className={styles.emptyInner}>
                <div
                    className={`${styles.iconWrap} ${isFailed ? styles.iconWrapFailed : ''}`}
                    aria-hidden>
                    {isFailed ? (
                        <DismissCircle24Filled className={styles.icon} />
                    ) : (
                        <ChatEmpty24Regular className={styles.icon} />
                    )}
                </div>
                <h2 className={styles.title}>{title}</h2>
                <p className={styles.message}>{message}</p>
                {metaLine ? <p className={styles.meta}>{metaLine}</p> : null}
                {canAct ? (
                    <button
                        type="button"
                        className={styles.primaryBtn}
                        disabled={generating}
                        onClick={handlePrimary}>
                        {generating ? 'Generating…' : actionLabel}
                    </button>
                ) : null}
            </div>
        </div>
    );
}
